/**
 * Marke „Entwicklung <Zahl>“ in der Kopfzeile — gemeinsame Regeln. (16.10.2026)
 *
 * Der Entwickler („Schmied“) trägt seine laufenden Arbeiten (Bauer, Einfädler, Prüfer) am Server ein (scripts/schmied-arbeit.mjs);
 * Martins App zeigt die Zahl als Marke, mit der Liste „woran, seit wann, Stufe“ darunter. Wie die Statuspille: KEIN Chat-Beitrag,
 * nicht in chat_messages, nie im Prompt von Vespera, nie Fremdtext-Markierung.
 */

/** Name des SSE-Ereignisses auf dem Chat-Strom. */
export const SCHMIED_ARBEIT_EREIGNIS = "schmied-arbeit";
export const SCHMIED_ARBEIT_STICHWORT_HOECHSTENS = 60;
/** 22.10.2026 (Martin: „Ich sehe nur einen Prozess“ — es laufen oft über zehn Hilfskräfte): 20 statt 12, „fertig“ mitgezählt. */
export const SCHMIED_ARBEIT_HOECHSTENS = 20;
/** 22.10.2026: „fertig“ bleibt so lange sichtbar, dann fällt der Eintrag heraus. Zählt nicht in die Zahl der Marke. */
export const SCHMIED_ARBEIT_FERTIG_SICHTBAR_MS = 10 * 60_000;
/** Obergrenze für den Verfall (Vorgabe war bis 19.10.2026 immer 4 Stunden); der Server nimmt nie mehr als das. */
export const SCHMIED_ARBEIT_VERFALL_MS = 4 * 3_600_000;
/**
 * 19.10.2026: Ohne Erneuerung (Eintragen, Stufe setzen, Abgleich) fällt ein Eintrag nach so vielen Minuten heraus, falls „fertig“ vergessen
 * wurde. Einstellbar mit SCHMIED_ARBEIT_VERFALL_MIN (1 bis 240).
 */
export const SCHMIED_ARBEIT_VERFALL_VORGABE_MIN = 30;
/** Ein Eintrag aus dem Abgleich (scripts/schmied-arbeit-abgleich.mjs): Kennzeichen des laufenden Agenten, nur Buchstaben und Ziffern. */
export const SCHMIED_ARBEIT_SCHLUESSEL = /^[A-Za-z0-9]{6,40}$/;

/**
 * Der Stand einer Hilfskraft. 22.10.2026: dazu „wartet“ (auf Martin) und „fertig“ (noch 10 Minuten sichtbar). Die Namen sind die Wörter,
 * die Martin in der Tafel liest — einfaches Deutsch, kein Fachwort. Der Schritt-Balken (bis 21.10.2026) ist weg: bei zehn Hilfskräften
 * war die Tafel eine Wand aus Balken.
 */
export const SCHMIED_ARBEIT_STUFEN = ["bauen", "test", "einfaedeln", "pruefen", "wartet", "fertig"] as const;
export type SchmiedArbeitStufe = (typeof SCHMIED_ARBEIT_STUFEN)[number];
export const SCHMIED_ARBEIT_STUFE_NAME: Record<SchmiedArbeitStufe, string> = {
  bauen: "baut",
  test: "testet",
  einfaedeln: "fädelt ein",
  pruefen: "prüft",
  wartet: "wartet auf dich",
  fertig: "fertig",
};
/** Zählt in die Zahl der Marke: alles außer „fertig“. */
export function istAktiv(stufe: SchmiedArbeitStufe): boolean {
  return stufe !== "fertig";
}
export const SCHMIED_ARBEIT_KENNUNG = /^[a-f0-9]{6}$/;

/**
 * 18.10.2026: die Kennung der MARKE „liest Privates mit · bis 09:15“ (server/dreier-privat-fenster.ts). Sie steht NUR im Arbeitsspeicher,
 * als letzter Eintrag der Liste, nie in der Ablage — und kein Aufruf des Schmieds (`add`, `setzeStufe`, `fertig`, Status, Beiträge)
 * kann sie verändern oder wegnehmen: allein das Fenster setzt und löscht sie. 3.11.2026: hierher gezogen, damit auch die Seite sie
 * erkennt — die Fortschrittskarte im Chat (frontend/lib/entwicklung-karte.ts) zeigt sie nicht, sie ist keine Arbeit.
 */
export const SCHMIED_MARKE_ID = "0a0a0a";

/**
 * 3.11.2026 (Fortschrittskarte im Chat): der Schritt als Satzanfang — „Prüfung läuft“, danach die Laufzeit. Feste Wörter, nie ein Titel.
 */
export const SCHMIED_ARBEIT_SCHRITT_NAME: Record<SchmiedArbeitStufe, string> = {
  bauen: "Bau läuft",
  test: "Test läuft",
  einfaedeln: "Einfädeln läuft",
  pruefen: "Prüfung läuft",
  wartet: "Wartet auf dich",
  fertig: "Fertig",
};
/** Wo eine laufende Arbeit steht, für die Kurzzeile der Karte: „3 im Bau“. „wartet“ und „fertig“ haben hier kein Wort. */
export const SCHMIED_ARBEIT_ORT_NAME: Partial<Record<SchmiedArbeitStufe, string>> = {
  bauen: "im Bau",
  test: "im Test",
  einfaedeln: "beim Einfädeln",
  pruefen: "in Prüfung",
};

/** Feste Wörter der Kurzliste in der Sofort-Quittung (27.10.2026) — nie ein Titel, nie Fremdtext. */
const HELFER_WORT: Partial<Record<SchmiedArbeitStufe, string>> = { bauen: "bauen", test: "testen", einfaedeln: "einfädeln", pruefen: "prüfen", wartet: "wartet" };
/** Längste Kurzliste; sonst nur „N Helfer“. */
export const HELFER_LISTE_HOECHSTENS = 34;

/**
 * „2 bauen, 1 prüfen“ — die laufenden Helfer nach Stufe gezählt, in fester Reihenfolge; „“ wenn keiner läuft (fertige zählen nicht).
 * Ist die Liste zu lang für eine Zeile: „5 Helfer“. Rein; nur Zahlen und feste Wörter.
 */
export function helferKurzliste(stufen: readonly SchmiedArbeitStufe[]): string {
  const teile: string[] = [];
  let summe = 0;
  for (const st of SCHMIED_ARBEIT_STUFEN) {
    const wort = HELFER_WORT[st];
    const n = stufen.filter((x) => x === st).length;
    if (!wort || n === 0) continue;
    summe += n;
    teile.push(`${n} ${wort}`);
  }
  if (summe === 0) return "";
  const liste = teile.join(", ");
  return liste.length <= HELFER_LISTE_HOECHSTENS ? liste : `${summe} Helfer`;
}

/**
 * Ein Eintrag, wie er über den Strom geht: `seitMs` = so lange läuft die Arbeit, gemessen am Server beim Senden; bei „fertig“ die ganze
 * Dauer bis zum Ende (sie wächst dann nicht mehr).
 */
export interface SchmiedArbeit {
  id: string;
  stichwort: string;
  stufe: SchmiedArbeitStufe;
  seitMs: number;
}
export interface SchmiedArbeitStand {
  arbeiten: SchmiedArbeit[];
}

export function istStufe(w: unknown): w is SchmiedArbeitStufe {
  return typeof w === "string" && (SCHMIED_ARBEIT_STUFEN as readonly string[]).includes(w);
}

/** Leerraum glätten, Steuerzeichen raus. */
export function ordneStichwort(t: string): string {
  // eslint-disable-next-line no-control-regex
  return t.replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim();
}

/** Der Stand aus einem SSE-Frame — nur die erwartete Form, sonst null; fehlerhafte Einträge fallen einzeln heraus. */
export function leseSchmiedArbeit(roh: unknown): SchmiedArbeitStand | null {
  if (!roh || typeof roh !== "object") return null;
  const liste = (roh as { arbeiten?: unknown }).arbeiten;
  if (!Array.isArray(liste)) return null;
  const arbeiten: SchmiedArbeit[] = [];
  for (const e of liste.slice(0, SCHMIED_ARBEIT_HOECHSTENS)) {
    if (!e || typeof e !== "object") continue;
    const { id, stichwort, stufe, seitMs } = e as Record<string, unknown>;
    if (typeof id !== "string" || !SCHMIED_ARBEIT_KENNUNG.test(id)) continue;
    if (typeof stichwort !== "string" || !istStufe(stufe)) continue;
    const s = ordneStichwort(stichwort);
    if (!s || s.length > SCHMIED_ARBEIT_STICHWORT_HOECHSTENS) continue;
    const seit = typeof seitMs === "number" && Number.isFinite(seitMs) ? Math.min(Math.max(seitMs, 0), SCHMIED_ARBEIT_VERFALL_MS) : 0;
    arbeiten.push({ id, stichwort: s, stufe, seitMs: seit });
  }
  return { arbeiten };
}
